import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { downloadCsv, fileTimestamp, toCsv } from '../../../lib/csv.js';
import {
  buildAreaCsvRows,
  compactArea,
  formatPoint,
  printAreaReport,
  reportFileBase,
  type CsvVariant,
  type ReportMeta,
  type ResolvedArea,
} from '../../../lib/performance-agg-ui.js';
import { BACKLOG_BASE } from './ChildTicketsModal.js';

export interface PerformanceReportModalProps {
  /** モーダル開閉状態 */
  open: boolean;
  /** 閉じるハンドラ */
  onClose: () => void;
  /** プレビュー対象の集計エリア（未選択時は null） */
  area: ResolvedArea | null;
  /** レポートメタ情報（未選択時は null） */
  meta: ReportMeta | null;
}

/**
 * 実績集計レポートのプレビュー。
 *
 * **出すのは母票（親チケ）単位の pt までで、子チケット明細は出さない。** PDF（`printAreaReport`）が
 * 元々この粒度で、プレビューだけ子票まで展開していると「プレビューで見たもの」と「出力したもの」が
 * 一致しない。子票の明細が要るときは CSV（`variant: 'full'`）で取る。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 実績集計レポートの大モーダルプレビュー。事業→母票の pt 明細をテーマ色付きで表示し（子チケット明細は出さず PDF と同じ粒度に揃える）、CSV出力とブラウザ印刷（PDF）を提供
 * @graph-connects buildAreaCsvRows [calls] 集計エリアをCSV行へ変換
 * @graph-connects downloadCsv [calls] CSVダウンロード起動
 * @graph-connects printAreaReport [calls] 印刷用ウィンドウ起動（PDF出力）
 */
export function PerformanceReportModal({ open, onClose, area, meta }: PerformanceReportModalProps) {
  const { t } = useTranslation();

  // 実績=0 の事業/母票/子チケットはプレビュー・CSV・PDF いずれも非表示にする。
  const view = useMemo(() => (area ? compactArea(area) : null), [area]);

  if (!view || !meta) return null;

  const handleCsv = (variant: CsvVariant = 'full') => {
    const suffix = variant === 'parentOnly' ? '_親のみ' : '';
    const base = `${reportFileBase(meta)}${suffix}_${fileTimestamp()}`;
    downloadCsv(`${base}.csv`, toCsv(buildAreaCsvRows(view, meta, variant)));
  };

  const handlePdf = () => {
    const ok = printAreaReport(view, meta, `${reportFileBase(meta)}_${fileTimestamp()}`);
    if (!ok) {
      window.alert(t('pmoBoard.performanceAgg.popupBlocked'));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="full"
      title={
        <span>
          {meta.areaLabel}
          <span className="ml-2 text-sm font-normal text-[var(--color-text-muted)]">
            {t('pmoBoard.performanceAgg.reportTitleSuffix', { mode: meta.modeLabel })}
          </span>
        </span>
      }
      description={`${meta.startDate} 〜 ${meta.endDate}`}
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            {t('pmoBoard.performanceAgg.close')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              handleCsv('full');
            }}
          >
            {t('pmoBoard.performanceAgg.exportCsv')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              handleCsv('parentOnly');
            }}
          >
            {t('pmoBoard.performanceAgg.exportCsvParent')}
          </Button>
          <Button variant="primary" onClick={handlePdf}>
            {t('pmoBoard.performanceAgg.exportPdf')}
          </Button>
        </div>
      }
    >
      <div className="space-y-1">
        <div className="text-base font-bold text-[var(--color-text-primary)]">
          {t('pmoBoard.performanceAgg.grandTotal', {
            point: formatPoint(view.totalPoint),
            count: view.ticketCount,
          })}
        </div>
      </div>

      {view.sections.length === 0 ? (
        <p className="py-12 text-center text-sm text-[var(--color-text-muted)]">
          {t('pmoBoard.performanceAgg.noResult')}
        </p>
      ) : (
        <div className="mt-4 space-y-6">
          {view.sections.map((section) => (
            <section key={section.data.business} className="space-y-3">
              <div
                className="flex items-center justify-between rounded-md px-3 py-2 text-base font-bold"
                style={{ backgroundColor: section.theme.color, color: section.theme.textColor }}
              >
                <span>{section.data.business}</span>
                {/* 件数は母票数。子票明細を出さなくなったので、PDF レポートと同じ粒度に揃える
                    （子チケット数のままだと画面に並ぶ行数と数字が合わない）。 */}
                <span className="text-sm font-semibold">
                  {t('pmoBoard.performanceAgg.businessTotal', {
                    point: formatPoint(section.data.totalPoint),
                    count: section.data.parents.length,
                  })}
                </span>
              </div>

              <div className="space-y-1">
                {section.data.parents.map((parent) => (
                  <div
                    key={parent.parentIssueKey}
                    className="flex items-baseline gap-2 border-b border-[var(--color-border)] pb-1"
                  >
                    <a
                      href={`${BACKLOG_BASE}${parent.parentIssueKey}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-semibold text-[var(--color-accent-primary)] hover:underline whitespace-nowrap"
                    >
                      {parent.parentIssueKey}
                    </a>
                    <span className="flex-1 text-sm text-[var(--color-text-primary)] truncate">
                      {parent.parentSummary ?? '-'}
                    </span>
                    <span className="text-sm font-bold text-[var(--color-text-primary)] whitespace-nowrap">
                      {formatPoint(parent.totalPoint)} pt
                    </span>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </Modal>
  );
}
